/**
 * Mapping from i18next language codes to date-fns locales.
 * Used for calendar and date formatting across the app.
 */
import {
    enUS,
    ru,
    de,
    fr,
    es,
    pl,
    it,
    pt,
    nl,
    sv,
    da,
    nb, // Norwegian uses 'nb' (Bokmål) in date-fns
    ar,
    bg,
    hr,
    cs,
    id,
    hu,
    tr,
    zhCN,
    uk,
    vi,
    fi,
} from "date-fns/locale";
import type { Locale } from "date-fns";

/**
 * Map of i18n language codes to date-fns locale objects.
 */
const DATE_LOCALES: Record<string, Locale> = {
    en: enUS,
    ru: ru,
    de: de,
    fr: fr,
    es: es,
    pl: pl,
    it: it,
    pt: pt,
    nl: nl,
    sv: sv,
    da: da,
    no: nb, // Norwegian Bokmål
    ar: ar,
    bg: bg,
    hr: hr,
    cs: cs,
    id: id,
    hu: hu,
    tr: tr,
    zh: zhCN,
    uk: uk,
    vi: vi,
    fi: fi,
};

/**
 * Get the date-fns locale for a given i18n language code.
 * Falls back to English if the language is not supported.
 */
export function getDateLocale(langCode: string): Locale {
    // Handle full locale codes like 'en-US' -> 'en'
    const shortCode = langCode.slice(0, 2).toLowerCase();
    return DATE_LOCALES[shortCode] || enUS;
}

/**
 * Get the Luxon locale string for a given i18n language code.
 * Luxon uses standard locale strings.
 */
export function getLuxonLocale(langCode: string): string {
    const shortCode = langCode.slice(0, 2).toLowerCase();
    // Luxon uses standard locale codes, map any special cases
    const luxonMap: Record<string, string> = {
        no: "nb", // Norwegian Bokmål
        zh: "zh-CN",
    };
    return luxonMap[shortCode] || shortCode;
}
